import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  deleteUser,
} from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { CheckCircle, AlertTriangle, Shield, Trash2, X, Megaphone, ArrowRight } from "lucide-react";

import { auth, db } from "../../firebase.js";
import { getStatsOptOut, setStatsOptOut } from "../../emailPrefs.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-modal="true" className="relative bg-bg-surface rounded-lg shadow-xl max-w-modal w-full animate-slide-up p-6">
        {children}
      </div>
    </div>
  );
}

function Card({ children, title, className = "" }) {
  return (
    <div className={`card p-6 ${className}`}>
      {title && <h3 className="text-lg font-heading font-bold mb-5">{title}</h3>}
      {children}
    </div>
  );
}

function FormField({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-base">
        {label}
        {required && <span className="text-error ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function Account() {
  const user = auth.currentUser;
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [profilePending, setProfilePending] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordPending, setPasswordPending] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const [verifyPending, setVerifyPending] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [statsEmails, setStatsEmails] = useState(true);
  const [statsPrefLoaded, setStatsPrefLoaded] = useState(false);
  const [statsPrefPending, setStatsPrefPending] = useState(false);
  const [statsPrefMessage, setStatsPrefMessage] = useState(null);

  const [hasService, setHasService] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) return;
    getStatsOptOut(user.uid)
      .then((optOut) => {
        if (!cancelled) setStatsEmails(!optOut);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStatsPrefLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) return;
    getDocs(query(collection(db, "services"), where("uid", "==", user.uid)))
      .then((snap) => {
        if (!cancelled) setHasService(!snap.empty);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  async function onToggleStatsEmails() {
    const next = !statsEmails;
    setStatsEmails(next);
    setStatsPrefPending(true);
    setStatsPrefMessage(null);
    try {
      await setStatsOptOut(user.uid, !next);
      setStatsPrefMessage({
        type: "success",
        text: next ? "Je ontvangt weer statistiek-updates." : "Je bent afgemeld voor statistiek-updates.",
      });
    } catch (err) {
      console.error(err);
      setStatsEmails(!next);
      setStatsPrefMessage({ type: "error", text: "Het opslaan van je voorkeur is mislukt. Probeer het opnieuw." });
    }
    setStatsPrefPending(false);
  }

  async function onSaveProfile(e) {
    e.preventDefault();
    setProfilePending(true);
    setProfileMessage(null);
    try {
      await updateProfile(user, { displayName: displayName.trim() });
      setProfileMessage({ type: "success", text: "Uw profiel is bijgewerkt." });
    } catch (err) {
      console.error(err);
      setProfileMessage({ type: "error", text: "Het bijwerken van uw profiel is mislukt. Probeer het opnieuw." });
    }
    setProfilePending(false);
  }

  async function onSendVerification() {
    setVerifyPending(true);
    setVerifyMessage(null);
    try {
      await sendEmailVerification(user);
      setVerifyMessage({ type: "success", text: "Verificatie-email is verzonden. Controleer uw inbox." });
    } catch (err) {
      console.error(err);
      if (err.code === "auth/too-many-requests") {
        setVerifyMessage({ type: "error", text: "Er is al recent een email verzonden. Probeer het over een paar minuten opnieuw." });
      } else {
        setVerifyMessage({ type: "error", text: "Het verzenden is mislukt. Probeer het later opnieuw." });
      }
    }
    setVerifyPending(false);
  }

  async function onChangePassword(e) {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword.length < 8) {
      setPasswordMessage({ type: "error", text: "Het nieuwe wachtwoord moet minimaal 8 tekens zijn." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: "error", text: "De wachtwoorden komen niet overeen." });
      return;
    }

    setPasswordPending(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage({ type: "success", text: "Uw wachtwoord is gewijzigd." });
    } catch (err) {
      console.error(err);
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPasswordMessage({ type: "error", text: "Het huidige wachtwoord is onjuist." });
      } else if (err.code === "auth/weak-password") {
        setPasswordMessage({ type: "error", text: "Het nieuwe wachtwoord is te zwak. Kies een sterker wachtwoord." });
      } else {
        setPasswordMessage({ type: "error", text: "Het wijzigen van het wachtwoord is mislukt. Probeer het opnieuw." });
      }
    }
    setPasswordPending(false);
  }

  async function onDeleteAccount() {
    setDeleteError("");
    setDeletePending(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      await deleteUser(user);
      navigate("/login");
    } catch (err) {
      console.error(err);
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setDeleteError("Het wachtwoord is onjuist.");
      } else {
        setDeleteError("Het verwijderen is mislukt. Probeer het opnieuw.");
      }
    }
    setDeletePending(false);
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold">Account</h1>
        <p className="text-sm text-text-secondary mt-1">Beheer uw profiel en accountinstellingen.</p>
      </div>

      <div className="space-y-6">
        <Card title="Profiel">
          <form onSubmit={onSaveProfile} className="space-y-5">
            <FormField label="Naam">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Uw naam"
                className="input-base"
              />
            </FormField>

            <div>
              <span className="label-base">Email</span>
              <div className="flex items-center gap-3">
                <span className="text-base text-text-primary">{user?.email}</span>
                {user?.emailVerified ? (
                  <span className="badge bg-success-subtle text-success">
                    <CheckCircle size={12} className="mr-1" />
                    Geverifieerd
                  </span>
                ) : (
                  <span className="badge bg-warning-subtle text-warning">
                    <AlertTriangle size={12} className="mr-1" />
                    Niet geverifieerd
                  </span>
                )}
              </div>
              {!user?.emailVerified && (
                <div className="mt-3">
                  {verifyMessage && (
                    <div className="mb-2">
                      <Message variant={verifyMessage.type} text={verifyMessage.text} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={onSendVerification}
                    disabled={verifyPending}
                    className="text-sm text-accent font-medium hover:underline underline-offset-2"
                  >
                    {verifyPending ? "Verzenden..." : "Verificatie-email opnieuw verzenden"}
                  </button>
                </div>
              )}
            </div>

            {profileMessage && <Message variant={profileMessage.type} text={profileMessage.text} />}

            <div className="flex justify-end pt-2">
              <Button type="submit" label="Opslaan" pending={profilePending} loadingLabel="Opslaan..." />
            </div>
          </form>
        </Card>

        <Card title="Wachtwoord wijzigen">
          <form onSubmit={onChangePassword} className="space-y-5">
            <FormField label="Huidig wachtwoord" required>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="input-base"
              />
            </FormField>

            <FormField label="Nieuw wachtwoord" required>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength="8"
                required
                className="input-base"
              />
              <p className="text-xs text-text-tertiary mt-1.5">Minimaal 8 tekens</p>
            </FormField>

            <FormField label="Wachtwoord bevestigen" required>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength="8"
                required
                className="input-base"
              />
            </FormField>

            {passwordMessage && <Message variant={passwordMessage.type} text={passwordMessage.text} />}

            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                label="Wachtwoord wijzigen"
                icon={Shield}
                variant="secondary"
                pending={passwordPending}
                loadingLabel="Wijzigen..."
              />
            </div>
          </form>
        </Card>

        <Card title="E-mailvoorkeuren">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-base text-text-primary font-medium">Statistiek-updates</p>
              <p className="text-sm text-text-secondary mt-1">
                Ontvang periodiek een overzicht van de statistieken van je locatie(s).
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={statsEmails}
              aria-label="Statistiek-updates ontvangen"
              disabled={!statsPrefLoaded || statsPrefPending}
              onClick={onToggleStatsEmails}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                statsEmails ? "bg-accent" : "bg-border"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-fast ${
                  statsEmails ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          {statsPrefMessage && (
            <div className="mt-4">
              <Message variant={statsPrefMessage.type} text={statsPrefMessage.text} />
            </div>
          )}
        </Card>

        {hasService && (
          <Card title="Mijn dienst">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Megaphone size={20} strokeWidth={1.5} className="text-accent shrink-0 mt-0.5" />
                <p className="text-sm text-text-secondary">
                  Beheer je vermelding als dienst: werk je gegevens, foto's en werkgebied bij.
                </p>
              </div>
              <Link to="/mijn-dienst" className="shrink-0">
                <Button label="Bewerken" icon={ArrowRight} variant="secondary" />
              </Link>
            </div>
          </Card>
        )}

        <div className="card border-error/20 bg-error-subtle p-6">
          <h3 className="text-lg font-heading font-bold text-error mb-2">Gevarenzone</h3>
          <p className="text-sm text-text-secondary mb-4">
            Uw account en alle bijbehorende gegevens worden permanent verwijderd. Deze actie kan niet ongedaan gemaakt worden.
          </p>
          <Button variant="destructive-ghost" label="Account verwijderen" icon={Trash2} onClick={() => setDeleteModalOpen(true)} />
        </div>
      </div>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-heading font-bold text-error">Account verwijderen</h3>
          <button
            onClick={() => setDeleteModalOpen(false)}
            aria-label="Sluiten"
            className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-text-secondary mb-5">Weet u het zeker? Voer uw wachtwoord in om uw account permanent te verwijderen.</p>

        <FormField label="Wachtwoord" required>
          <input
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            autoComplete="current-password"
            className="input-base"
          />
        </FormField>

        {deleteError && (
          <div className="mt-3">
            <Message text={deleteError} />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-5">
          <Button variant="secondary" label="Annuleren" onClick={() => setDeleteModalOpen(false)} />
          <Button
            variant="destructive-ghost"
            label="Definitief verwijderen"
            icon={Trash2}
            pending={deletePending}
            loadingLabel="Verwijderen..."
            onClick={onDeleteAccount}
          />
        </div>
      </Modal>
    </div>
  );
}

export default Account;
